import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "@/components/ui/sonner";
import LandingPage from './pages/LandingPage';
import WizardPage from './pages/WizardPage';
import CategoryPage from './pages/CategoryPage';
import ThanksPage from './pages/ThanksPage';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminCaseDetail from './pages/AdminCaseDetail';
import AdminSettings from './pages/AdminSettings';
import AdminCalibration from './pages/AdminCalibration';
import { AboutPage, TermsPage, ContactPage, PrivacyPage, CookiesPage, UserPolicyPage } from './pages/StaticPages';
import { getCategoryBySlug } from './lib/categoryConfig';
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const isAuth = localStorage.getItem("admin_token");
  if (!isAuth) {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

// The guide needs a chosen service; old links without one go to the service list.
const WizardRoute = () => {
  const [searchParams] = useSearchParams();
  if (!getCategoryBySlug(searchParams.get('category'))) {
    return <Navigate to="/#tjanster" replace />;
  }
  return <WizardPage />;
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  React.useEffect(() => {
    // Scroll is handled here; stop the browser from restoring an old position over it.
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);

  React.useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return undefined;
    }
    // Wait a tick so the target section has rendered after a redirect.
    const timer = window.setTimeout(() => {
      document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ behavior: 'auto', block: 'start' });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [pathname, hash]);

  return null;
};

function App() {
  return (
    <HelmetProvider>
      <div className="App font-sans text-slate-900">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Main */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/wizard" element={<WizardRoute />} />
            <Route path="/tack" element={<ThanksPage />} />

            {/* Category pages */}
            <Route path="/målning" element={<CategoryPage slug="malning" />} />
            <Route path="/kök" element={<CategoryPage slug="kok" />} />
            <Route path="/våtrum" element={<CategoryPage slug="vatrum" />} />
            <Route path="/kakel" element={<CategoryPage slug="kakel" />} />
            <Route path="/golv" element={<CategoryPage slug="golv" />} />
            <Route path="/vvs" element={<CategoryPage slug="vvs" />} />
            <Route path="/el" element={<CategoryPage slug="el" />} />
            <Route path="/snickeri" element={<CategoryPage slug="snickeri" />} />

            {/* Admin */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cases/:id"
              element={
                <ProtectedRoute>
                  <AdminCaseDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/calibration"
              element={
                <ProtectedRoute>
                  <AdminCalibration />
                </ProtectedRoute>
              }
            />

            {/* Static Pages */}
            <Route path="/om-oss" element={<AboutPage />} />
            <Route path="/villkor" element={<TermsPage />} />
            <Route path="/kontakt" element={<ContactPage />} />
            <Route path="/integritet" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/anvandarpolicy" element={<UserPolicyPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </HelmetProvider>
  );
}

export default App;
